import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Stack,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";

import RoomDayWiseCard from "./RoomDayWiseCard";
import TotalMismatchConfirmModal from "./TotalMismatchConfirmModal";
import {
  initializeAllRoomsData,
  getConfirmedRooms,
  calculateDayWiseTotal,
  validateRoomRates,
  buildPayload,
} from "./dayWiseRatesUtils";
import { useProSidebar } from "react-pro-sidebar";
import { getRoomOnlyTotal } from "../../../utils";

const DayWiseRatesModal = ({
  open,
  onClose,
  bookingDetails,
  onSave,
  loading = false,
  colors,
}) => {
  const [roomsData, setRoomsData] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { collapsed } = useProSidebar();

    const SIDEBAR_EXPANDED_WIDTH = 250;
  const SIDEBAR_COLLAPSED_WIDTH = 80;
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  // Get only confirmed rooms
  const confirmedRooms = useMemo(
    () => getConfirmedRooms(bookingDetails?.rooms),
    [bookingDetails?.rooms]
  );

  // Reset state when modal opens
  React.useEffect(() => {
    if (open && confirmedRooms.length > 0) {
      const bookingTotal = getRoomOnlyTotal(bookingDetails);
      const initialData = initializeAllRoomsData(confirmedRooms, bookingTotal);
      setRoomsData(initialData);
      setShowValidationErrors(false);
      setConfirmModalOpen(false);
    }
  }, [open, confirmedRooms, bookingDetails]);

  // Calculate totals
  const { dayWiseTotal, bookingTotal, hasMismatch, difference } = useMemo(() => {
    const dayWiseTotal = calculateDayWiseTotal(roomsData);
    const bookingTotal = getRoomOnlyTotal(bookingDetails);
    const difference = dayWiseTotal - bookingTotal;

    return {
      dayWiseTotal,
      bookingTotal,
      hasMismatch: dayWiseTotal !== bookingTotal,
      difference,
    };
  }, [roomsData, bookingDetails]);

  // Handle individual field change
  const handleDetailChange = useCallback(
    (roomId, dateTimestamp, field, value) => {
      setRoomsData((prev) => ({
        ...prev,
        [roomId]: prev[roomId].map((detail) =>
          detail.date === dateTimestamp ? { ...detail, [field]: value } : detail
        ),
      }));
    },
    []
  );

  // Apply same rate to all days for a room
  const handleApplySameRate = useCallback((roomId) => {
    setRoomsData((prev) => {
      const roomDetails = prev[roomId];
      if (!roomDetails || roomDetails.length === 0) return prev;

      const template = roomDetails[0];
      const updatedDetails = roomDetails.map((detail) => ({
        ...detail,
        amount: template.amount,
        extra_bed_price: template.extra_bed_price,
        meal_plan_price: template.meal_plan_price,
      }));

      return { ...prev, [roomId]: updatedDetails };
    });
  }, []);

  // Clear all rates for a room
  const handleClearRates = useCallback((roomId) => {
    setRoomsData((prev) => ({
      ...prev,
      [roomId]: prev[roomId].map((detail) => ({
        ...detail,
        amount: "",
        extra_bed_price: "",
        meal_plan_price: "",
      })),
    }));
  }, []);

  // Handle save click
  const handleSaveClick = useCallback(() => {
    if (!validateRoomRates(roomsData)) {
      setShowValidationErrors(true);
      return;
    }

    setShowValidationErrors(false);

    // If totals match, save directly
    if (!hasMismatch) {
      const payload = buildPayload({
        bookingId: bookingDetails?.booking_id,
        roomsData,
        overrideTotal: false,
        dayWiseTotal,
        bookingTotal,
      });
      onSave(payload);
      return;
    }

    // If mismatch, show confirmation modal
    setConfirmModalOpen(true);
  }, [roomsData, hasMismatch, bookingDetails, dayWiseTotal, bookingTotal, onSave]);

  // Handle confirm from modal
  const handleConfirmSave = useCallback(() => {
    const payload = buildPayload({
      bookingId: bookingDetails?.booking_id,
      roomsData,
      overrideTotal: true,
      dayWiseTotal,
      bookingTotal,
    });
    onSave(payload);
    setConfirmModalOpen(false);
  }, [bookingDetails, roomsData, dayWiseTotal, bookingTotal, onSave]);

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        // maxWidth="lg"
         fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: `calc(100vw - ${sidebarWidth + 48}px)`,
            maxWidth: `calc(100vw - ${sidebarWidth + 48}px)`,
            marginLeft: `${sidebarWidth}px`,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: colors.blueAccent?.[700] || "#1976d2",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Manage day-wise rates for all assigned rooms
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={loading}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: colors.primary[500], pt: 3 }}>
          {/* Info Alert */}
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Room Rate is required</strong> for all days. Extra Bed and
              Meal Plan are optional.
            </Typography>
          </Alert>

          {/* Validation Error Alert */}
          {showValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Please fill in <strong>Room Rate</strong> for all days in all
                rooms before saving.
              </Typography>
            </Alert>
          )}

          {/* Totals Summary */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: colors.primary[400],
              border: `1px solid ${colors.grey?.[700] || "#424242"}`,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  color={colors.grey?.[300] || "text.secondary"}
                >
                  Current Booking Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#fff">
                  ₹{bookingTotal.toLocaleString("en-IN")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  color={colors.grey?.[300] || "text.secondary"}
                >
                  Day-wise Total
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: !hasMismatch
                      ? colors.greenAccent?.[400] || "#4caf50"
                      : difference > 0
                      ? colors.redAccent?.[400] || "#ef5350"
                      : colors.blueAccent?.[400] || "#42a5f5",
                  }}
                >
                  ₹{dayWiseTotal.toLocaleString("en-IN")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  color={colors.grey?.[300] || "text.secondary"}
                >
                  Difference
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color:
                      difference === 0
                        ? colors.greenAccent?.[400] || "#4caf50"
                        : difference > 0
                        ? colors.redAccent?.[400] || "#ef5350"
                        : colors.blueAccent?.[400] || "#42a5f5",
                  }}
                >
                  {difference >= 0 ? "+" : ""}₹
                  {difference.toLocaleString("en-IN")}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Room Cards */}
          <Stack spacing={3}>
            {confirmedRooms.map((room) => (
              <RoomDayWiseCard
                key={room._id}
                room={room}
                dayWiseDetails={roomsData[room._id] || []}
                onDetailChange={handleDetailChange}
                onApplySameRate={handleApplySameRate}
                onClearRates={handleClearRates}
                showValidationErrors={showValidationErrors}
                colors={colors}
              />
            ))}
          </Stack>

          {confirmedRooms.length === 0 && (
            <Typography
              color={colors.grey?.[300] || "text.secondary"}
              textAlign="center"
              py={4}
            >
              No confirmed rooms found in this booking.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: colors.primary[500] }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSaveClick}
            disabled={loading || confirmedRooms.length === 0}
            startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
            sx={{ px: 3, fontWeight: "bold" }}
          >
            {loading ? "Saving..." : "Save Day-wise Rates"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal for Total Mismatch */}
      <TotalMismatchConfirmModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmSave}
        dayWiseTotal={dayWiseTotal}
        bookingTotal={bookingTotal}
        loading={loading}
        colors={colors}
      />
    </>
  );
};

export default DayWiseRatesModal;